/**
 * SnusWe — Privacy notice (draft for legal / DPO review).
 * Single controller (SnusWe GmbH · Austria); tables styled via .lg-table-wrap in PrivacyPolicy.jsx
 */

export const PRIVACY_LAST_UPDATED = "2026-04-24";

const TABLE_WRAP_START = '<div class="lg-table-wrap"><table>';
const TABLE_WRAP_END = "</table></div>";

export const PRIVACY_BODY_EN = `
<p>This privacy notice describes how <strong>SnusWe GmbH</strong> (“we”, “us”) collects, uses, and shares personal data when you use <a href="https://snuswe.com">www.snuswe.at</a>, create an account, place orders, contact customer service, or subscribe to our newsletter.</p>
<p><strong>Personal data</strong> means any information relating to an identified or identifiable person (for example your name, e-mail address, or online identifiers).</p>
<p>We take appropriate technical and organisational measures to protect personal data and to process it in line with the EU General Data Protection Regulation (<abbr>GDPR</abbr>) and the Austrian Data Protection Act (<abbr title="Datenschutzgesetz">DSG</abbr>). This notice is a summary; please also review our <a href="/en/terms">Terms &amp; Conditions</a> where relevant.</p>
<h2 id="toc">Contents</h2>
<ul>
<li><a href="#who">Who is responsible?</a></li>
<li><a href="#what">What personal data we collect</a></li>
<li><a href="#sources">Where we collect it from</a></li>
<li><a href="#why">Why we use it</a></li>
<li><a href="#legal-bases">Legal bases</a></li>
<li><a href="#recipients">Who we share with</a></li>
<li><a href="#rights">Your rights</a></li>
<li><a href="#cookies">Cookies and similar technologies</a></li>
<li><a href="#transfers">International transfers</a></li>
<li><a href="#updates">Updates</a></li>
<li><a href="#contact">Contact & supervisory authority</a></li>
<li><a href="#details-use">Details: purposes, data, basis, retention</a></li>
<li><a href="#details-share">Details: sharing with recipients</a></li>
</ul>

<h2 id="who">1. Who is responsible for your personal data?</h2>
<p>The controller responsible for the processing described in this notice is <strong>SnusWe GmbH</strong>, Austria (“we”, “us”), operating <a href="https://snuswe.com">www.snuswe.at</a>. We decide why and how your personal data is processed when you use our website, create an account, place orders, or contact customer service.</p>
<p>We do not sell your personal data. Processors (e.g. hosting, e-mail, payments, carriers) process data on our instructions and under contract.</p>
${TABLE_WRAP_START}
<thead><tr><th>Controller</th><th>Website</th><th>Delivery</th></tr></thead>
<tbody>
<tr><td>SnusWe GmbH, Austria</td><td><a href="https://snuswe.com">www.snuswe.at</a></td><td>Primarily Austria (as offered at checkout)</td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="what">2. What personal data do we collect?</h2>
<p>We only collect what we need for the purposes below. Depending on how you use our services, this may include:</p>
<ul>
<li><strong>Identity:</strong> name, date of birth where required for age checks.</li>
<li><strong>Contact:</strong> e-mail, phone, billing/delivery address.</li>
<li><strong>Orders &amp; delivery:</strong> basket, order lines, prices, payment status, shipping method, tracking.</li>
<li><strong>Account &amp; activity:</strong> login identifiers, preferences, session and usage data on our site.</li>
<li><strong>Communications:</strong> messages you send us (e-mail, forms, chat).</li>
<li><strong>Technical:</strong> IP address, device/browser type, approximate location from IP, logs for security.</li>
<li><strong>Marketing:</strong> newsletter subscription and marketing preferences where you opt in.</li>
</ul>

<h2 id="sources">3. Where do we collect personal data from?</h2>
<ul>
<li><strong>You</strong> — when you register, order, subscribe to the newsletter, or contact us.</li>
<li><strong>Payment providers</strong> — status and limited identifiers needed to complete payment (we do not store full card numbers where the provider tokenises them).</li>
<li><strong>Carriers</strong> — delivery status and tracking data.</li>
<li><strong>IT and analytics tools</strong> — as configured on our site (see cookies).</li>
</ul>

<h2 id="why">4. Why do we use your personal data?</h2>
<p>Examples include: processing and delivering your order; managing subscriptions; customer service; account and website functionality; fraud prevention and security; complying with law (e.g. tax, product rules, age-restricted sales); improving our shop and statistics in aggregated form; and — where you have opted in — marketing e-mails.</p>

<h2 id="legal-bases">5. Legal bases (<abbr title="General Data Protection Regulation">GDPR</abbr> Art. 6)</h2>
<p>We rely on one or more of the following, depending on the processing:</p>
<ul>
<li><strong>Contract</strong> — processing necessary to take steps at your request or to perform our contract (e.g. fulfil an order).</li>
<li><strong>Legal obligation</strong> — e.g. accounting, tax, or regulatory requirements.</li>
<li><strong>Legitimate interests</strong> — e.g. IT security, service improvement, limited direct marketing to existing customers where permitted, and asserting legal claims; we balance these against your rights.</li>
<li><strong>Consent</strong> — e.g. non-essential cookies, newsletter, or optional profiling where we ask for it; you may withdraw consent at any time.</li>
</ul>

<h2 id="recipients">6. Who do we share personal data with?</h2>
<p>Categories of recipients may include:</p>
${TABLE_WRAP_START}
<thead><tr><th>Recipient type</th><th>Role / examples</th></tr></thead>
<tbody>
<tr><td>Payment service providers</td><td>Processing payments (e.g. card, PayPal, Klarna, cash-on-delivery flows as offered).</td></tr>
<tr><td>Shipping / logistics</td><td>Delivery and returns (e.g. Austrian Post, DHL).</td></tr>
<tr><td>IT &amp; hosting</td><td>Website, e-mail, backups, security.</td></tr>
<tr><td>Professional advisers</td><td>Lawyers, auditors where required.</td></tr>
<tr><td>Authorities</td><td>When we are legally required to disclose information.</td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="rights">7. Your rights</h2>
<p>Under the GDPR and Austrian law you may have the right to: access; rectification; erasure; restriction; objection (including to certain marketing); data portability (where applicable); and to lodge a complaint with a supervisory authority. Where processing is based on consent, you may withdraw consent at any time.</p>
<p>To exercise your rights, contact us (see <a href="#contact">Contact</a> below). You may also contact the Austrian Data Protection Authority (<abbr title="Datenschutzbehörde">DSB</abbr>).</p>

<h2 id="cookies">8. Cookies and similar technologies</h2>
<p>We use cookies and similar technologies for essential site functions, preferences, analytics, and — with your consent — marketing. You can control non-essential cookies via our cookie banner or your browser settings. More detail can be provided in a separate cookie policy or table linked from the site footer when available.</p>

<h2 id="transfers">9. International transfers</h2>
<p>Where we use providers outside the <abbr title="European Economic Area">EEA</abbr>, we ensure appropriate safeguards (e.g. Standard Contractual Clauses and supplementary measures where required) unless an adequacy decision applies.</p>

<h2 id="updates">10. Updates to this notice</h2>
<p>We may update this notice to reflect changes in our processing or the law. The “Last updated” date at the top will change; for material changes we will use reasonable means to inform you (e.g. a notice on the website or e-mail where appropriate).</p>

<h2 id="contact">11. Contact &amp; supervisory authority</h2>
${TABLE_WRAP_START}
<thead><tr><th>Topic</th><th>Details</th></tr></thead>
<tbody>
<tr><td>Customer service &amp; privacy requests</td><td><a href="mailto:support@snuswe.at">support@snuswe.at</a> · <a href="tel:+4312345678">+43 1 234 5678</a> (Mon–Fri 9:00–17:00 CET)</td></tr>
<tr><td>Supervisory authority (Austria)</td><td>Österreichische Datenschutzbehörde — <a href="https://www.dsb.gv.at/" rel="noopener noreferrer" target="_blank">dsb.gv.at</a></td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="details-use">12. Details: purposes, categories, legal basis, storage</h2>
<p>The table below summarises typical processing on <a href="https://snuswe.com">www.snuswe.at</a>. Not every row may apply to you.</p>
${TABLE_WRAP_START}
<thead><tr><th>Purpose</th><th>Categories (examples)</th><th>Legal basis</th><th>Storage (indicative)</th></tr></thead>
<tbody>
<tr><td>Process &amp; deliver orders / subscriptions</td><td>Identity, contact, order &amp; payment data, delivery data</td><td>Contract</td><td>For the duration of fulfilment plus statutory limitation / warranty and accounting periods.</td></tr>
<tr><td>Customer service</td><td>Identity, contact, order data, communication content</td><td>Contract; legitimate interests (service quality)</td><td>Usually up to 18 months after the ticket is closed unless longer retention is justified.</td></tr>
<tr><td>Account &amp; “My account”</td><td>Identity, contact, order history, preferences</td><td>Contract; legitimate interests</td><td>While the account is active; thereafter as required by law or for claims.</td></tr>
<tr><td>Age verification (where used)</td><td>Identity, technical data, verification outcome; sometimes ID images only if you consent</td><td>Legal obligation / contract; consent for sensitive steps</td><td>As long as needed to complete verification; minimal retention of proof where required.</td></tr>
<tr><td>Newsletter &amp; marketing (opt-in)</td><td>Contact, preferences, engagement metrics</td><td>Consent</td><td>Until you unsubscribe; then suppression data as needed.</td></tr>
<tr><td>Website analytics &amp; improvement (non-essential)</td><td>Technical, usage identifiers</td><td>Consent where required</td><td>Per cookie/tool settings.</td></tr>
<tr><td>Security, fraud prevention, logs</td><td>Technical, usage, identity where relevant</td><td>Legitimate interests; legal obligation</td><td>Short security logs (e.g. months) unless longer needed for an incident.</td></tr>
<tr><td>Legal compliance (tax, product law)</td><td>Order &amp; invoice data</td><td>Legal obligation</td><td>As required (e.g. multi-year retention for accounting).</td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="details-share">13. Details: sharing for main purposes</h2>
${TABLE_WRAP_START}
<thead><tr><th>Recipient</th><th>Purpose (summary)</th><th>Typical data</th><th>Legal basis</th></tr></thead>
<tbody>
<tr><td>Payment providers</td><td>Authorisation &amp; settlement</td><td>Order ID, amount, billing contact, tokens</td><td>Contract</td></tr>
<tr><td>Carriers</td><td>Delivery / returns</td><td>Name, address, phone, parcel data</td><td>Contract</td></tr>
<tr><td>Hosting / IT vendors</td><td>Operation of the site and e-mail</td><td>Data you submit to the site</td><td>Contract; legitimate interests</td></tr>
</tbody>
${TABLE_WRAP_END}
<p><strong>Last updated:</strong> ${PRIVACY_LAST_UPDATED}.</p>
`;

export const PRIVACY_BODY_DE = `
<p>Diese Datenschutzinformation beschreibt, wie die <strong>SnusWe GmbH</strong> („wir“) personenbezogene Daten verarbeitet, wenn Sie <a href="https://snuswe.com">www.snuswe.at</a> nutzen, ein Konto anlegen, bestellen, den Kundendienst kontaktieren oder unseren Newsletter abonnieren.</p>
<p><strong>Personenbezogene Daten</strong> sind alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen (z. B. Name, E-Mail-Adresse oder Online-Kennungen).</p>
<p>Wir treffen angemessene technische und organisatorische Maßnahmen zum Schutz der Daten und verarbeiten sie im Einklang mit der <abbr title="Datenschutz-Grundverordnung">DSGVO</abbr> und dem österreichischen <abbr title="Datenschutzgesetz">DSG</abbr>. Ergänzend gelten unsere <a href="/de/terms">AGB</a>, soweit einschlägig.</p>
<h2 id="toc">Inhalt</h2>
<ul>
<li><a href="#who">Wer ist verantwortlich?</a></li>
<li><a href="#what">Welche personenbezogenen Daten</a></li>
<li><a href="#sources">Woher stammen die Daten?</a></li>
<li><a href="#why">Wofür nutzen wir sie?</a></li>
<li><a href="#legal-bases">Rechtsgrundlagen</a></li>
<li><a href="#recipients">Empfänger</a></li>
<li><a href="#rights">Ihre Rechte</a></li>
<li><a href="#cookies">Cookies</a></li>
<li><a href="#transfers">Drittlandübermittlungen</a></li>
<li><a href="#updates">Änderungen</a></li>
<li><a href="#contact">Kontakt &amp; Aufsichtsbehörde</a></li>
<li><a href="#details-use">Details: Zwecke, Daten, Rechtsgrundlage</a></li>
<li><a href="#details-share">Details: Weitergabe</a></li>
</ul>

<h2 id="who">1. Wer ist für Ihre Daten verantwortlich?</h2>
<p>Verantwortlicher im Sinne der <abbr title="Datenschutz-Grundverordnung">DSGVO</abbr> ist die <strong>SnusWe GmbH</strong>, Österreich („wir“), Betreiberin von <a href="https://snuswe.com">www.snuswe.at</a>. Wir entscheiden über Zweck und Mittel der Verarbeitung, wenn Sie unsere Website nutzen, ein Konto anlegen, bestellen oder den Kundendienst kontaktieren.</p>
<p>Wir verkaufen keine personenbezogenen Daten. Auftragsverarbeiter (z. B. Hosting, E-Mail, Zahlung, Versand) verarbeiten Daten nur nach Weisung und vertraglich gebunden.</p>
${TABLE_WRAP_START}
<thead><tr><th>Verantwortliche</th><th>Website</th><th>Lieferung</th></tr></thead>
<tbody>
<tr><td>SnusWe GmbH, Österreich</td><td><a href="https://snuswe.com">www.snuswe.at</a></td><td>vorwiegend Österreich (wie im Checkout angeboten)</td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="what">2. Welche Daten erheben wir?</h2>
<p>Je nach Nutzung u. a.:</p>
<ul>
<li><strong>Identität:</strong> Name, ggf. Geburtsdatum für Altersprüfungen.</li>
<li><strong>Kontakt:</strong> E-Mail, Telefon, Rechnungs-/Lieferadresse.</li>
<li><strong>Bestellung &amp; Lieferung:</strong> Warenkorb, Positionen, Preise, Zahlstatus, Versandart, Sendungsverfolgung.</li>
<li><strong>Konto &amp; Nutzung:</strong> Anmeldedaten, Einstellungen, Nutzungs- und Protokolldaten.</li>
<li><strong>Kommunikation:</strong> Inhalte von E-Mails, Formularen, Chat.</li>
<li><strong>Technisch:</strong> IP-Adresse, Gerät/Browser, ungefähre Standortableitung aus der IP, Sicherheitslogs.</li>
<li><strong>Marketing:</strong> Newsletter-Einwilligung, Präferenzen.</li>
</ul>

<h2 id="sources">3. Herkunft der Daten</h2>
<ul>
<li><strong>Von Ihnen</strong> bei Registrierung, Bestellung, Newsletter, Kontakt.</li>
<li><strong>Zahlungsdienstleister</strong> für Zahlungsabwicklung.</li>
<li><strong>Transportdienstleister</strong> für Zustellung und Retouren.</li>
<li><strong>IT-/Analyse-Tools</strong> gemäß Cookie-Einstellungen.</li>
</ul>

<h2 id="why">4. Zwecke der Verarbeitung</h2>
<p>z. B. Vertragsabwicklung und Lieferung, Abonnements, Kundenservice, Kontofunktionen, Sicherheit und Betrugsprävention, gesetzliche Pflichten, Reichweitenmessung in aggregierter Form sowie — bei Einwilligung — Marketing per E-Mail.</p>

<h2 id="legal-bases">5. Rechtsgrundlagen (Art. 6 DSGVO)</h2>
<ul>
<li><strong>Vertrag</strong> — zur Erfüllung des Kaufvertrags.</li>
<li><strong>Rechtliche Verpflichtung</strong> — z. B. Steuer- und Handelsrecht.</li>
<li><strong>Berechtigte Interessen</strong> — z. B. IT-Sicherheit, Verbesserung des Shops, begrenztes Direktmarketing an Bestandskunden soweit zulässig; Interessenabwägung.</li>
<li><strong>Einwilligung</strong> — z. B. nicht notwendige Cookies, Newsletter; widerruflich.</li>
</ul>

<h2 id="recipients">6. Empfänger</h2>
${TABLE_WRAP_START}
<thead><tr><th>Art</th><th>Beispiele / Rolle</th></tr></thead>
<tbody>
<tr><td>Zahlungsdienstleister</td><td>Karte, PayPal, Klarna, Nachnahme je nach Angebot.</td></tr>
<tr><td>Logistik</td><td>Post AT, DHL u. a.</td></tr>
<tr><td>IT &amp; Hosting</td><td>Website, E-Mail, Backups.</td></tr>
<tr><td>Berater</td><td>Recht, Wirtschaftsprüfung bei Bedarf.</td></tr>
<tr><td>Behörden</td><td>bei gesetzlicher Verpflichtung.</td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="rights">7. Ihre Rechte</h2>
<p>Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch (u. a. gegen bestimmtes Marketing), Datenübertragbarkeit soweit anwendbar, Beschwerde bei einer Aufsichtsbehörde, Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft.</p>
<p>Kontakt siehe unten; Aufsichtsbehörde in Österreich: <strong>Datenschutzbehörde (DSB)</strong>.</p>

<h2 id="cookies">8. Cookies</h2>
<p>Wir setzen Cookies und ähnliche Technologien für notwendige Funktionen, Präferenzen und — mit Einwilligung — Statistik/Marketing. Steuerung über Cookie-Banner bzw. Browsereinstellungen. Eine gesonderte Cookie-Tabelle kann verlinkt werden.</p>

<h2 id="transfers">9. Drittlandübermittlungen</h2>
<p>Bei Dienstleistern außerhalb des <abbr title="Europäischer Wirtschaftsraum">EWR</abbr> setzen wir geeignete Garantien (z. B. Standardvertragsklauseln und ergänzende Maßnahmen) ein, sofern keine Angemessenheitsentscheidung vorliegt.</p>

<h2 id="updates">10. Änderungen</h2>
<p>Wir passen diese Hinweise an, wenn sich Verarbeitung oder Recht ändert. Das Datum „Stand“ oben wird aktualisiert; bei wesentlichen Änderungen informieren wir angemessen.</p>

<h2 id="contact">11. Kontakt &amp; Aufsichtsbehörde</h2>
${TABLE_WRAP_START}
<thead><tr><th>Thema</th><th>Angaben</th></tr></thead>
<tbody>
<tr><td>Kundenservice &amp; Betroffenenrechte</td><td><a href="mailto:support@snuswe.at">support@snuswe.at</a> · <a href="tel:+4312345678">+43 1 234 5678</a> (Mo–Fr 9:00–17:00 MEZ)</td></tr>
<tr><td>Aufsichtsbehörde</td><td>Österreichische Datenschutzbehörde — <a href="https://www.dsb.gv.at/" rel="noopener noreferrer" target="_blank">dsb.gv.at</a></td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="details-use">12. Details: Zwecke, Kategorien, Rechtsgrundlage, Speicherung</h2>
${TABLE_WRAP_START}
<thead><tr><th>Zweck</th><th>Kategorien (Beispiele)</th><th>Rechtsgrundlage</th><th>Speicherung (indikativ)</th></tr></thead>
<tbody>
<tr><td>Bestellung &amp; Abo</td><td>Identität, Kontakt, Bestell-, Zahlungs-, Lieferdaten</td><td>Vertrag</td><td>Erfüllung zuzüglich gesetzlicher Fristen / Gewährleistung / Buchführung.</td></tr>
<tr><td>Kundenservice</td><td>Identität, Kontakt, Bestellung, Inhalte</td><td>Vertrag; berechtigtes Interesse</td><td>in der Regel bis zu 18 Monate nach Abschluss des Vorgangs, sofern nicht länger erforderlich.</td></tr>
<tr><td>Kundenkonto</td><td>Identität, Kontakt, Bestellhistorie</td><td>Vertrag; berechtigtes Interesse</td><td>solange das Konto besteht; danach nach gesetzlichen Vorgaben.</td></tr>
<tr><td>Altersprüfung (falls genutzt)</td><td>Identität, technische Daten, Ergebnis; ggf. Ausweisbild nur mit Einwilligung</td><td>Vertrag / gesetzliche Pflicht; Einwilligung für besondere Schritte</td><td>so lange wie zur Prüfung nötig.</td></tr>
<tr><td>Newsletter (opt-in)</td><td>Kontakt, Präferenzen</td><td>Einwilligung</td><td>bis Abmeldung; ggf. Sperrdaten.</td></tr>
<tr><td>Analyse (nicht notwendig)</td><td>technische Nutzungsdaten</td><td>Einwilligung</td><td>gemäß Tool-/Cookie-Einstellungen.</td></tr>
<tr><td>Sicherheit</td><td>technische Daten, ggf. Identität</td><td>berechtigtes Interesse; Rechtspflicht</td><td>kurze Logfristen, länger bei Vorfällen.</td></tr>
<tr><td>Rechtliche Pflichten</td><td>Rechnungs- und Belegdaten</td><td>Rechtliche Verpflichtung</td><td>gesetzliche Aufbewahrungsfristen.</td></tr>
</tbody>
${TABLE_WRAP_END}

<h2 id="details-share">13. Details: Weitergabe</h2>
${TABLE_WRAP_START}
<thead><tr><th>Empfänger</th><th>Zweck</th><th>Typische Daten</th><th>Rechtsgrundlage</th></tr></thead>
<tbody>
<tr><td>Zahlungsdienstleister</td><td>Autorisierung, Abwicklung</td><td>Bestellreferenz, Betrag, Rechnungskontakt, Token</td><td>Vertrag</td></tr>
<tr><td>Transport</td><td>Zustellung / Retoure</td><td>Name, Adresse, Telefon, Sendungsdaten</td><td>Vertrag</td></tr>
<tr><td>Hosting / IT</td><td>Betrieb Website &amp; E-Mail</td><td>von Ihnen eingegebene Daten</td><td>Vertrag; berechtigtes Interesse</td></tr>
</tbody>
${TABLE_WRAP_END}
<p><strong>Stand:</strong> ${PRIVACY_LAST_UPDATED}.</p>
`;
